// src/pos-app/surfaces/waiter-sales/WaiterSalesSurface.js
//
// F15 part 2 — the native Waiter Sales surface. Mounted by WaiterSalesStation.
//
// WHY THIS EXISTS. The web hub offers Cashier Sales and Table Service as
// separate stations, and carries an orphaned WaiterSalesPage at
// /functions/waiter-sales that no tile points at. The native app had neither:
// its station labelled "Waiter" mounts TablesSurface — floor plan, waitlist,
// reservations, merge tables — which is Table Service under the wrong name
// (stations/WaiterStation.js:9-15 says so outright). So a waiter walking a
// floor with a phone had no surface built for them, which is why the phone
// waiter experience was never merely "unstyled" but absent.
//
// Table Service stays a tablet surface. This is the phone one.
//
// ASSEMBLY, NOT INVENTION. Every part below already existed and is reused
// unmodified except CartPanel, which gained ONE prop:
//   table picking          <- surfaces/cashier/TableSelect
//   catalogue + search     <- surfaces/cashier/CatalogBrowse
//   cart lines / notes     <- surfaces/cashier/CartPanel (mode="waiter")
//   cart + catalogue state <- hooks/pos/useOrderBuilder, useProductCatalog
//   add-ons                <- components/pos/modals/AddonSelectionModal
//                             (reached through CatalogBrowse, zero edits)
//   SUBMIT ENGINE          <- surfaces/waiter/lib/drawerFlow.submitOrder,
//                             byte-for-byte unchanged
//
// The submit engine matters most: waiter -> cashier already works end to end
// (drawerFlow -> tenantStorage.saveOrder -> POST /tenant-storage/orders, which
// the cashier's usePendingOrders polls from the same tenant-scoped table).
// Reimplementing submit here would have forked a working money path for no
// reason. This surface supplies UI and a ctx; drawerFlow does the rest.
//
// NO PAYMENT. Waiter orders are held for the cashier to settle —
// drawerFlow writes paymentMethod:'' / is_submitted:false, exactly as the web
// WaiterSalesPage does (WaiterSalesPage.js:749,753). A waiter who must take
// payment uses a Cashier station. This keeps the surface small and keeps every
// money path in one place.
//
// THREE STEPS, ONE AT A TIME (rebuild). The first cut stacked the dining pill
// row, the table picker and the catalogue onto one 390px-wide screen, which is
// cramped — and it carried a hard bug: TAPPING A TABLE DID NOTHING.
//
// The bug's cause is worth recording because the fix is not where you'd look.
// TableSelect's tile tap (TableSelect.js:204) calls setTableNumber(num) and
// nothing else — it never touches setTableNumberConfirmed. Only its
// random-number variant confirms, from its own Confirm button / Enter key
// (TableSelect.js:296,305). The old gate here was
// `selectedDiningOption && tableNumber && tableNumberConfirmed`, so a tile tap
// set two of three conditions and the surface never moved. The catalogue was
// unreachable by the primary path. So this surface now watches tableNumber
// itself and does NOT wait on tableNumberConfirmed (see the STEP 2 -> 3 effect).
// TableSelect is left byte-for-byte alone: it is the cashier surface's picker
// too, and changing its selection semantics would move the cashier's floor.
//
// The steps mirror the legacy web page (pages/pos/WaiterSalesPage.js:38 —
// `const [currentStep, setCurrentStep] = useState(1)`), which is the flow a
// waiter already knows:
//   1  dining option   — full-width rows, one tap
//   2  table           — the reused <TableSelect/>, unchanged
//   3  products        — <CatalogBrowse/> + cart bar + cart sheet
// with a back affordance at every step, the chosen option/table carried
// forward as context, and a 1-2-3 indicator so the waiter is never lost.
//
// LAYOUT (HARD no-scroll rule, same as SellSurface.js:7-14). A flex column
// that fills the station's content slot exactly:
//   [step header: back + title/context + 1-2-3]        flex:none
//   [stage: options OR table select OR catalogue]      flex:1 min-height:0
//   [cart bar: count + total + review]  (step 3 only)  flex:none
// plus a scrim + slide-up sheet hosting CartPanel, both position:absolute
// against .wss-root, which is the containing block AND overflow:hidden — so
// the overlay pair is bounded by the surface and nothing page-scrolls. This
// mirrors SellSurface's F15 phone frame deliberately: the pattern is proven on
// this exact UI and the two surfaces should not drift.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useLanguage, getEntityTranslation, translateDynamic } from '../../../context/LanguageContext';
import useDiningSetup from '../../../hooks/pos/useDiningSetup';
import useBusinessDay from '../../../hooks/pos/useBusinessDay';
import useProductCatalog from '../../../hooks/pos/useProductCatalog';
import useOrderBuilder from '../../../hooks/pos/useOrderBuilder';
import usePendingOrders from '../../../hooks/pos/usePendingOrders';
import { modifierService, occupationService } from '../../../services/api';
import CatalogBrowse from '../cashier/CatalogBrowse';
import CartPanel from '../cashier/CartPanel';
import TableSelect from '../cashier/TableSelect';
import { money, cartItemCount } from '../cashier/lib/money';
import { submitOrder } from '../waiter/lib/drawerFlow';

const CSS = `
.wss-root{flex:1;height:100%;min-height:0;display:flex;flex-direction:column;
  overflow:hidden;box-sizing:border-box;position:relative;}

.wss-toast{flex:none;margin:10px 16px 0;padding:9px 13px;border-radius:12px;
  font-size:13px;font-weight:700;}
.wss-toast.err{background:var(--c-redLight);border:1px solid var(--c-redBorder);color:var(--c-red);}
.wss-toast.ok{background:var(--c-greenLight);border:1px solid var(--c-greenBorder);color:var(--c-green);}

/* ── Step header ──────────────────────────────────────────────────────────
   Back (≥44px) | what this step is + what has been chosen so far | 1-2-3.
   One row, so the stage below keeps almost the whole phone screen. */
.wss-head{flex:none;display:flex;align-items:center;gap:10px;padding:12px 16px 10px;}
.wss-back{flex:none;display:inline-flex;align-items:center;gap:6px;
  min-height:44px;min-width:44px;padding:0 13px;border-radius:13px;cursor:pointer;
  font-family:inherit;font-size:13px;font-weight:800;
  background:var(--c-surface);border:1px solid var(--c-border);color:var(--c-slate);}
.wss-back:hover{border-color:var(--c-borderHover);}
/* Logical mirror: the arrow must point back, not left, under RTL. */
[dir="rtl"] .wss-back svg{transform:scaleX(-1);}
.wss-head-main{flex:1;min-width:0;}
.wss-head-t{font-size:16px;font-weight:800;letter-spacing:-.01em;color:var(--c-ink);
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.wss-head-s{font-size:11.5px;font-weight:600;color:var(--c-muted);
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}

/* 1-2-3 indicator. Past steps fill in, the current one is outlined so the
   waiter can see both where they are and how far in. */
.wss-steps{flex:none;display:flex;align-items:center;gap:6px;margin:0;padding:0;
  list-style:none;}
.wss-dot{width:22px;height:22px;border-radius:50%;display:flex;align-items:center;
  justify-content:center;font-size:11px;font-weight:800;font-variant-numeric:tabular-nums;
  background:var(--c-faint);border:1px solid var(--c-border);color:var(--c-mutedLight);}
.wss-dot.done{background:var(--c-green);border-color:var(--c-green);color:#fff;}
.wss-dot.now{background:var(--c-greenLight);border-color:var(--c-green);color:var(--c-greenDark);}

/* ── Orders tab: this shift's open tickets ─────────────────────────────── */
.wss-orders{flex:1;min-height:0;overflow-y:auto;display:flex;flex-direction:column;
  gap:8px;padding:12px 0 12px;}
.wss-order{display:flex;align-items:center;gap:12px;width:100%;min-height:64px;
  padding:12px 14px;box-sizing:border-box;border-radius:14px;cursor:pointer;
  text-align:start;font-family:inherit;
  background:var(--c-surface);border:1px solid var(--c-border);
  transition:border-color .12s ease, transform .12s ease;}
.wss-order:active{transform:scale(.99);}
.wss-order:hover{border-color:var(--c-greenBorder);}
.wss-order-main{flex:1;min-width:0;display:flex;flex-direction:column;gap:2px;}
.wss-order-l1{font-size:15px;font-weight:800;color:var(--c-ink);
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.wss-order-l2{font-size:11.5px;font-weight:600;color:var(--c-muted);
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.wss-order-total{flex:none;font-size:15px;font-weight:800;color:var(--c-ink);
  font-variant-numeric:tabular-nums;}

.wss-stage{flex:1;min-height:0;display:flex;padding:0 16px 0;}
.wss-content{flex:1;min-width:0;min-height:0;display:flex;flex-direction:column;}
.wss-empty{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;
  gap:8px;text-align:center;color:var(--c-muted);padding:24px;}
.wss-empty-t{font-size:15px;font-weight:800;color:var(--c-slate);}
.wss-empty-s{font-size:13px;font-weight:600;max-width:280px;line-height:1.5;}

/* ── Step 1 · dining option ───────────────────────────────────────────────
   Full-width rows, not chips. A waiter picks this once per order with one
   thumb while standing up; 60px rows are the tap target that deserves. */
.wss-pills{flex:1;min-height:0;overflow-y:auto;align-content:start;
  display:flex;flex-direction:column;gap:10px;padding:2px 0 8px;}
.wss-pill{position:relative;flex:none;display:flex;align-items:center;gap:12px;
  width:100%;min-height:60px;padding:0 18px;border-radius:16px;cursor:pointer;
  text-align:start;font-family:inherit;font-size:16px;font-weight:800;letter-spacing:-.01em;
  background:var(--c-surface);border:1px solid var(--c-border);color:var(--c-ink);
  box-shadow:0 1px 3px rgba(15,23,42,.06);
  transition:transform .12s, box-shadow .12s, border-color .12s;}
.wss-pill:hover{border-color:var(--c-greenBorder);transform:translateY(-1px);
  box-shadow:0 4px 14px color-mix(in srgb, var(--c-green) 16%, transparent);}
.wss-pill.active{border-color:var(--c-green);background:var(--c-greenLight);
  color:var(--c-greenDark);}
.wss-pill-label{flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.wss-pill-go{flex:none;display:flex;color:var(--c-mutedLight);}
[dir="rtl"] .wss-pill-go{transform:scaleX(-1);}

/* Cart bar — same anatomy as SellSurface's .slf-cartbar. No safe-area inset
   here: the station's <BottomTabs/> sits below and already pads by
   --safe-bottom, and adding it in both places double-counts (the exact bug
   F15 shipped on the cashier surface). */
.wss-cartbar{flex:none;display:flex;align-items:center;gap:12px;width:100%;
  min-height:64px;padding:10px 14px;
  border-top:1px solid color-mix(in srgb, var(--c-border) 45%, transparent);
  text-align:start;cursor:pointer;
  /* Material from the shared .aq-liquid class — same chrome recipe as the
     station bars and the cashier cart bar. */}
.wss-cb-count{flex:none;min-width:34px;height:34px;padding:0 9px;border-radius:11px;
  background:var(--c-green);color:#fff;font-size:14px;font-weight:800;
  display:flex;align-items:center;justify-content:center;font-variant-numeric:tabular-nums;}
.wss-cb-lines{flex:1;min-width:0;display:flex;flex-direction:column;gap:1px;}
.wss-cb-l1{font-size:11px;font-weight:800;letter-spacing:.06em;text-transform:uppercase;
  color:var(--c-muted);}
.wss-cb-l2{font-size:18px;font-weight:800;letter-spacing:-.02em;color:var(--c-ink);
  font-variant-numeric:tabular-nums;}
.wss-cb-go{flex:none;min-height:44px;padding:0 16px;border-radius:13px;
  background:var(--c-green);color:#fff;font-size:14px;font-weight:800;
  display:flex;align-items:center;gap:8px;}

/* Same z-index reasoning as SellSurface.js: .wss-root is position:relative
   with z-index:auto so it creates no stacking context, and these must sit
   BELOW the body-level portals around them (CartPanel's promo sheet 40/50,
   AddonSelectionModal via Modal.js). 5/6 covers the stage and nothing more. */
.wss-scrim{position:absolute;inset:0;z-index:5;
  background:color-mix(in srgb, var(--c-ink) 42%, transparent);
  animation:wssScrimIn .2s ease;}
.wss-sheet{position:absolute;left:0;right:0;bottom:0;z-index:6;
  height:86%;max-height:86%;display:flex;flex-direction:column;overflow:hidden;
  background:var(--c-surface);border-radius:22px 22px 0 0;
  border-top:1px solid color-mix(in srgb, var(--c-border) 55%, transparent);
  box-shadow:0 -18px 50px color-mix(in srgb, var(--c-ink) 22%, transparent);
  animation:wssSheetIn .28s cubic-bezier(.32,.72,0,1);}
@keyframes wssScrimIn{from{opacity:0;}to{opacity:1;}}
@keyframes wssSheetIn{from{transform:translateY(100%);}to{transform:translateY(0);}}
@media (prefers-reduced-motion: reduce){
  .wss-scrim,.wss-sheet{animation:none;}
}
.wss-grab{flex:none;width:100%;min-height:44px;display:flex;align-items:center;
  justify-content:center;cursor:pointer;}
.wss-grab i{display:block;width:38px;height:4px;border-radius:999px;
  background:color-mix(in srgb, var(--c-border) 90%, transparent);}
.wss-sheet-body{flex:1;min-height:0;display:flex;flex-direction:column;overflow:hidden;}
`;

const ChevronUpIcon = (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M18 15l-6-6-6 6" />
    </svg>
);

const ArrowBackIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M15 18l-6-6 6-6" />
    </svg>
);

const ChevronNextIcon = (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M9 18l6-6-6-6" />
    </svg>
);


// "Does this dining option have tables?" — i.e. its table mode is anything
// other than "No tables" (Settings → Sales → Dining Options → Edit).
//
// useDiningSetup.processTableData normalises the three modes to:
//   Fixed tables      -> an array of table numbers
//   Any table number  -> the string 'random_number'
//   No tables         -> whatever the tenant config held: null, '', or []
//
// That last case is why this is NOT a straight reuse of CartPanel's
// `requiresTableNumber` (`cfg != null && cfg !== ''`). That check keeps an
// EMPTY ARRAY, because [] is neither null nor ''. On the cashier surface that
// is harmless; here it would put a counter option in front of a waiter and
// dead-end them at a table picker with nothing to pick.
export function hasTables(tableConfig) {
    if (tableConfig == null || tableConfig === '') return false;
    if (Array.isArray(tableConfig)) return tableConfig.length > 0;
    return true; // 'random_number'
}

export default function WaiterSalesSurface({
    // Mirrors SellSurface's contract so WaiterSalesStation can drive an Orders
    // pane the same way CashierStation does.
    ordersTabActive = false,
    onRequestSellTab,
} = {}) {
    const { tenantConfig, username, tenantId } = useAuth();
    const { t, language, translationDetails } = useLanguage();
    const currency = tenantConfig?.currency_symbol || '£';

    const {
        diningOptions, diningOptionObjects, fullDiningOptions, availableTableNumbers,
        selectedDiningOption, setSelectedDiningOption,
        tableNumber, setTableNumber,
        tableNumberConfirmed, setTableNumberConfirmed,
        tableViewMode, setTableViewMode,
        loadDiningOptions, refreshDiningOptions,
    } = useDiningSetup(tenantConfig);

    const { isBusinessDay, businessDaySalesDate } = useBusinessDay();

    const [error, setError] = useState('');

    // Composed exactly as SellSurface composes them (SellSurface.js:395-406) —
    // the builder needs the catalogue's stock and product list to enforce its
    // own stock guard, so the order of these two matters.
    const catalog = useProductCatalog(tenantConfig);
    const builder = useOrderBuilder({
        tenantConfig,
        selectedDiningOption,
        diningOptionObjects,
        bulkCalculatedStock: catalog.bulkCalculatedStock,
        products: catalog.products,
        setError,
        t,
    });

    const {
        pendingOrders, printedOrders, loadPendingOrders,
    } = usePendingOrders(isBusinessDay, businessDaySalesDate);

    const [success, setSuccess] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [cartOpen, setCartOpen] = useState(false);
    const [activeOccupations, setActiveOccupations] = useState([]);
    const catalogLoadedRef = useRef(false);

    // 1 dining option -> 2 table -> 3 products. Named exactly as the legacy web
    // page names it (WaiterSalesPage.js:38) so the two flows read the same.
    const [step, setStep] = useState(1);

    // Both toasts auto-dismiss on the same 2.5s rule SellSurface converged on
    // (see its header, "user-QC finding 4") rather than the legacy pages'
    // inconsistent 2000-3000ms — one rule, one place.
    useEffect(() => {
        if (!error && !success) return undefined;
        const id = setTimeout(() => { setError(''); setSuccess(''); }, 2500);
        return () => clearTimeout(id);
    }, [error, success]);

    // Mount only — deliberately NOT [loadDiningOptions].
    //
    // useDiningSetup declares loadDiningOptions as a plain async function, so
    // it gets a fresh identity on every render. Depending on it re-ran this
    // effect every render, which called setDiningOptionObjects with a NEW
    // object, which re-triggered useOrderBuilder's
    // "auto-apply service charge from the dining option" effect (it depends on
    // diningOptionObjects) — force-resetting addServiceCharge to the option's
    // default. The visible symptom in QA was the Service Charge checkbox
    // "fake ticking" and snapping straight back to unticked, plus a constant
    // background reload. SellSurface has always used [] here for the same
    // reason (SellSurface.js:822-826).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { loadDiningOptions(); }, []);

    // Catalogue loads ONCE, same catalogLoadedRef pattern OrderDrawer uses —
    // a waiter reopens this surface constantly across a shift and refetching
    // the whole menu each time is the kind of thing that makes a POS feel slow
    // on a phone network.
    useEffect(() => {
        if (catalogLoadedRef.current) return;
        catalogLoadedRef.current = true;
        catalog.loadCatalog();
        (async () => {
            try {
                const cat = await modifierService.getCatalog();
                if (builder.setModifierCatalog) builder.setModifierCatalog(cat);
            } catch (e) {
                console.error('Failed to load modifier catalog:', e);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Occupations drive TableSelect's occupied/free badges. Polled on the same
    // 10s cadence the cashier surface uses so a waiter sees a table another
    // waiter just seated.
    useEffect(() => {
        if (!isBusinessDay) return undefined;
        let cancelled = false;
        const pull = async () => {
            try {
                const list = await occupationService.listActive();
                if (!cancelled) setActiveOccupations(list || []);
            } catch (e) {
                // Non-fatal: the table grid still works without badges.
                console.error('Failed to load occupations:', e);
            }
        };
        pull();
        const id = setInterval(pull, 10000);
        return () => { cancelled = true; clearInterval(id); };
    }, [isBusinessDay]);

    useEffect(() => { loadPendingOrders(); }, [loadPendingOrders]);

    const totals = builder.calculateTotal
        ? builder.calculateTotal()
        : (builder.memoizedTotals || { subtotal: 0, serviceCharge: 0, tax: 0, total: 0 });

    const cartCount = cartItemCount(builder.selectedProducts || []);
    const cartTotal = totals?.total || 0;

    const optionLabel = (option) =>
        getEntityTranslation(diningOptionObjects?.[option], language, translationDetails)
        || translateDynamic(option, t);

    // Only dining options that HAVE tables belong on this surface. A waiter
    // takes orders at a table; a counter option has no table to stand at, and
    // picking one here would strand the flow — TableSelect would have nothing
    // to offer and drawerFlow needs a tableNumber to build its tableKey
    // (`${diningOptionName}-${tableNumber}`).
    //
    // The rule is exactly "table mode is not 'No tables'" (Settings → Sales →
    // Dining Options → Edit offers Fixed tables / Any table number / No
    // tables). So Dine In, Dine Out, Juice Shop, Talabat and Uber all qualify;
    // only Takeaway-style counter options drop out.
    //
    // See hasTables() above for why this is stricter than CartPanel's
    // `requiresTableNumber` rather than a straight reuse of it.
    const tableDiningOptions = useMemo(
        () => diningOptions.filter((option) => hasTables(availableTableNumbers?.[option])),
        [diningOptions, availableTableNumbers],
    );

    // If the tenant has configured no table-service option at all, say so
    // rather than render an empty pill row that looks broken.
    const hasTableOptions = tableDiningOptions.length > 0;

    // A previously-selected option that is no longer offered here (e.g. the
    // hook defaulted to Takeaway) must not leave the surface in a state its
    // own pill row cannot represent.
    useEffect(() => {
        if (!selectedDiningOption) return;
        if (tableDiningOptions.includes(selectedDiningOption)) return;
        setSelectedDiningOption(tableDiningOptions[0] || '');
        setTableNumber('');
        setTableNumberConfirmed(false);
        setStep(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableDiningOptions, selectedDiningOption]);

    // The 'Any table number' scheme picks with a TEXT INPUT wired straight to
    // setTableNumber (TableSelect.js:294), so its tableNumber goes truthy on the
    // first keystroke. That one variant must wait for its own Confirm button.
    const isRandomOption = availableTableNumbers?.[selectedDiningOption] === 'random_number';

    // STEP 2 -> 3, and the whole point of this rebuild. TableSelect's tile tap
    // sets ONLY tableNumber (TableSelect.js:204) — it never confirms — so the
    // advance must watch tableNumber, not tableNumberConfirmed. Gating on
    // confirmation is exactly what made tapping a table do nothing before.
    useEffect(() => {
        if (!selectedDiningOption || !tableNumber) return;
        if (isRandomOption && !tableNumberConfirmed) return;
        setStep(3);
    }, [selectedDiningOption, tableNumber, tableNumberConfirmed, isRandomOption]);

    // What the stage actually shows. `step` is intent; this is intent clamped to
    // what the data can support, so a half-reset state (an option cleared out
    // from under step 2, a table cleared out from under step 3) can never render
    // a picker with nothing to pick or a catalogue with nowhere to send it.
    const stage = (() => {
        if (!selectedDiningOption || tableDiningOptions.length === 0) return 1;
        if (step >= 3 && tableNumber) return 3;
        if (step >= 2) return 2;
        return 1;
    })();

    // A table is "open" exactly when the product step is showing: an option is
    // chosen, a table is chosen, and the waiter has walked the wizard to it.
    const tableOpen = stage === 3;

    const tableKey = selectedDiningOption && tableNumber
        ? `${selectedDiningOption}-${tableNumber}`
        : null;
    const existingOrder = tableKey ? pendingOrders?.[tableKey] : null;
    // 'append' when this table already has an unsettled order — drawerFlow
    // blocks a second 'new' on a table whose occupation.first_order_at is set,
    // so getting this wrong surfaces as a hard error rather than a duplicate.
    const mode = existingOrder ? 'append' : 'new';
    const occupation = useMemo(
        () => activeOccupations.find(
            (o) => String(o.table_number) === String(tableNumber)
                && o.dining_option === selectedDiningOption,
        ) || null,
        [activeOccupations, tableNumber, selectedDiningOption],
    );

    // Captured at the moment the table opens, BEFORE any edit — drawerFlow
    // diffs this against the cart to decide which lines get a fresh KDS ticket
    // and which are already sent.
    const originalProductsRef = useRef([]);
    useEffect(() => {
        if (!tableOpen) return;
        if (existingOrder?.products) {
            const seed = JSON.parse(JSON.stringify(existingOrder.products));
            originalProductsRef.current = JSON.parse(JSON.stringify(existingOrder.products));
            builder.setSelectedProducts(seed);
            builder.setNotes(existingOrder.notes || '');
        } else {
            originalProductsRef.current = [];
            builder.setSelectedProducts([]);
            builder.setNotes('');
        }
        // Re-seed only when the target table changes, same rule OrderDrawer
        // applies — anything wider wipes a cart mid-entry.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableOpen, tableKey, existingOrder?.order_id]);

    const handleAddProduct = (product) => {
        if (!tableOpen) {
            setError(t('please_select_table_and_add_products'));
            return;
        }
        builder.handleProductWithAddons(product);
    };

    // Drop the current table and everything entered against it. Shared by the
    // two exits below so "go back" and "order sent" cannot clear different
    // things — the divergence that leaves a stale cart on the next table.
    const clearTable = () => {
        setTableNumberConfirmed(false);
        setTableNumber('');
        builder.setSelectedProducts([]);
        builder.setNotes('');
        setCartOpen(false);
    };

    // Step 3 -> 2. tableNumber is cleared deliberately: leaving it set would
    // mean re-tapping the SAME table changes nothing observable, so the advance
    // effect would not fire and the waiter would be stuck on the picker.
    const backToTables = () => { clearTable(); setStep(2); };

    // Step 2 -> 1. The option stays selected (it is the row the waiter will
    // most likely re-tap) but the table does not.
    const backToDiningOptions = () => {
        setTableNumberConfirmed(false);
        setTableNumber('');
        setStep(1);
    };

    // Where a finished order lands: back at the top, nothing carried over.
    const resetToStart = () => { clearTable(); setStep(1); };

    const goBack = () => {
        if (stage === 3) backToTables();
        else if (stage === 2) backToDiningOptions();
    };

    const chooseDiningOption = (option) => {
        setSelectedDiningOption(option);
        setTableNumber('');
        setTableNumberConfirmed(false);
        setStep(2);
    };

    // TableSelect calls this instead of setTableNumber when the tapped table
    // already carries an unsettled order (TableSelect.js:200-203) — the append
    // case. It hands back the `${option}-${number}` key, so the number is
    // recovered by prefix rather than by splitting on '-', which a dining option
    // name is free to contain.
    const openExistingOrder = (key) => {
        const prefix = `${selectedDiningOption}-`;
        const num = typeof key === 'string' && key.startsWith(prefix)
            ? key.slice(prefix.length)
            : '';
        if (!num) return;
        setTableNumber(num);
        setStep(3);
    };

    // The whole submit path is drawerFlow's. This only supplies the ctx it
    // documents, using the same field names OrderDrawer passes
    // (OrderDrawer.js:359-367) so the two callers cannot drift.
    const handleSend = () => submitOrder({
        mode,
        isBusinessDay,
        businessDaySalesDate,
        diningOptionName: selectedDiningOption,
        tableNumber,
        selectedProducts: builder.selectedProducts,
        notes: builder.notes,
        totals,
        addServiceCharge: builder.addServiceCharge,
        addTax: builder.addTax,
        occupation,
        existingOrder,
        originalProducts: originalProductsRef.current,
        tenantConfig,
        username,
        products: catalog.products,
        printableCategories: catalog.printableCategories,
        t,
        setError,
        setSubmitting,
        refreshBulkStock: catalog.refreshBulkStock,
        onOrderPlaced: () => {
            // Existing keys, deliberately — the legacy waiter drawer already
            // says exactly this and the DB already carries both in en + ar.
            setSuccess(mode === 'append'
                ? t('order_updated_successfully_sent_to_kitchen')
                : t('order_submitted_successfully_sent_to_kitchen'));
            loadPendingOrders();
        },
        onClose: () => {
            setCartOpen(false);
            resetToStart();
        },
    });

    // Escape closes the cart sheet. AddonSelectionModal is Modal.js-based and
    // keeps its own Escape self-dismissal, so this must not also fire while it
    // is open on top — same gate SellSurface's chain applies.
    useEffect(() => {
        if (!cartOpen) return undefined;
        const onKey = (e) => {
            if (e.key !== 'Escape') return;
            if (builder.addonModalOpen) return;
            setCartOpen(false);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [cartOpen, builder.addonModalOpen]);

    // drawerFlow saves diningOption + tableNumber on the order itself
    // (drawerFlow.js:258-259), so those are read directly. The key is only a
    // fallback for rows written by an older path — and it is matched by
    // PREFIX against the known options, never split on '-', because a dining
    // option name is free to contain a hyphen ("Dine-In").
    const tableFromKey = (key, option) => {
        const prefix = `${option || ''}-`;
        if (option && key.startsWith(prefix)) return key.slice(prefix.length);
        const match = diningOptions.find((o) => key.startsWith(`${o}-`));
        return match ? key.slice(match.length + 1) : key;
    };

    // Reopening puts the waiter straight on the product step for that table.
    // `mode` derives from pendingOrders, so this lands in 'append' and
    // drawerFlow patches the existing ticket rather than starting a new one.
    const reopenOrder = (key) => {
        const order = pendingOrders?.[key];
        if (!order) return;
        const option = order.diningOption || diningOptions.find((o) => key.startsWith(`${o}-`));
        const num = order.tableNumber || tableFromKey(key, option);
        if (!option || !num) return;
        setSelectedDiningOption(option);
        setTableNumber(String(num));
        setTableNumberConfirmed(true);
        setStep(3);
        if (onRequestSellTab) onRequestSellTab();
    };

    // The Orders tab lists THIS SHIFT'S OPEN ORDERS — the tables that already
    // have something sent to the kitchen and are not yet settled.
    //
    // It used to render <TableSelect/>, i.e. a second table picker, which is
    // what step 2 of the wizard already is. QA reported it as "clicking on
    // orders should show all current pending orders, not adding products",
    // and they were right: an Orders tab that asks you to pick a table is
    // answering a question nobody asked.
    //
    // Tapping a row reopens that table in APPEND mode, which is the whole
    // point of the tab — a waiter comes back to a table to add a round.
    if (ordersTabActive) {
        const openOrders = Object.entries(pendingOrders || {})
            .map(([key, order]) => ({ key, order }))
            .filter(({ order }) => order && !order.is_submitted)
            .sort((a, b) => String(b.order?.time || '').localeCompare(String(a.order?.time || '')));

        return (
            <div className="wss-root" data-surface="waiter-sales-orders">
                <style>{CSS}</style>
                <div className="wss-stage">
                    <div className="wss-content">
                        {openOrders.length === 0 ? (
                            <div className="wss-empty">
                                <div className="wss-empty-t">{t('no_pending_orders')}</div>
                                <div className="wss-empty-s">{t('open_orders_appear_here')}</div>
                            </div>
                        ) : (
                            <div className="wss-orders" role="list">
                                {openOrders.map(({ key, order }) => {
                                    const count = (order.products || [])
                                        .reduce((sum, p) => sum + (parseInt(p.quantity, 10) || 1), 0);
                                    return (
                                        <button
                                            key={key}
                                            type="button"
                                            role="listitem"
                                            className="wss-order"
                                            data-testid={`open-order-${key}`}
                                            onClick={() => reopenOrder(key)}
                                        >
                                            <span className="wss-order-main">
                                                <span className="wss-order-l1">
                                                    {t('table')} {order.tableNumber || tableFromKey(key, order.diningOption)}
                                                </span>
                                                <span className="wss-order-l2">
                                                    {order.diningOption || ''}
                                                    {order.time ? ` · ${order.time}` : ''}
                                                    {` · ${count} ${count === 1 ? t('item') : t('items')}`}
                                                </span>
                                            </span>
                                            <span className="wss-order-total money">
                                                {money(currency, order.total || 0)}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    const stepTitle = stage === 3
        ? t('add_products')
        : (stage === 2 ? t('select_table') : t('select_dining_option'));

    // What has been chosen so far, carried down the wizard so the waiter never
    // has to go back a step just to check which table they are standing at.
    const stepContext = stage === 3
        ? `${t('table')} ${tableNumber} · ${optionLabel(selectedDiningOption)}${mode === 'append' ? ` · ${t('add_items')}` : ''}`
        : (stage === 2
            ? optionLabel(selectedDiningOption)
            : (hasTableOptions ? t('select_dining_option_to_begin') : ''));

    return (
        <div className="wss-root" data-surface="waiter-sales" data-step={stage}>
            <style>{CSS}</style>

            {error && <div className="wss-toast err" role="alert">{error}</div>}
            {success && <div className="wss-toast ok" role="status">{success}</div>}

            <div className="wss-head">
                {stage > 1 && (
                    <button
                        type="button"
                        className="wss-back"
                        data-testid="wss-back"
                        /* WCAG "label in name": the accessible name must start
                           with the visible text, so the step-3 hint is appended
                           to it rather than replacing it. */
                        aria-label={stage === 3 ? `${t('back')} — ${t('change_table')}` : undefined}
                        onClick={goBack}
                    >
                        {ArrowBackIcon}
                        {t('back')}
                    </button>
                )}
                <div className="wss-head-main">
                    <div className="wss-head-t">{stepTitle}</div>
                    {stepContext && <div className="wss-head-s">{stepContext}</div>}
                </div>
                <ol
                    className="wss-steps"
                    data-testid="wss-steps"
                    role="progressbar"
                    aria-valuemin={1}
                    aria-valuemax={3}
                    aria-valuenow={stage}
                    aria-label={t('step_x_of_y', { current: stage, total: 3 })}
                >
                    {[1, 2, 3].map((n) => (
                        <li
                            key={n}
                            className={`wss-dot${n === stage ? ' now' : ''}${n < stage ? ' done' : ''}`}
                            data-step={n}
                            aria-current={n === stage ? 'step' : undefined}
                        >
                            {n}
                        </li>
                    ))}
                </ol>
            </div>

            <div className="wss-stage">
                <div className="wss-content">
                    {stage === 3 ? (
                        <CatalogBrowse
                            catalog={catalog}
                            builder={builder}
                            currency={currency}
                            onAddProduct={handleAddProduct}
                        />
                    ) : stage === 2 ? (
                        <TableSelect
                            selectedDiningOption={selectedDiningOption}
                            diningOptionObjects={diningOptionObjects}
                            fullDiningOptions={fullDiningOptions}
                            availableTableNumbers={availableTableNumbers}
                            tableNumber={tableNumber}
                            setTableNumber={setTableNumber}
                            setTableNumberConfirmed={setTableNumberConfirmed}
                            viewMode={tableViewMode}
                            setViewMode={setTableViewMode}
                            pendingOrders={pendingOrders}
                            printedOrders={printedOrders}
                            occupations={activeOccupations}
                            currency={currency}
                            onOpenOrder={openExistingOrder}
                            refreshDiningOptions={refreshDiningOptions}
                        />
                    ) : hasTableOptions ? (
                        <div className="wss-pills" role="group" aria-label={t('dining_options')}>
                            {tableDiningOptions.map((option) => (
                                <button
                                    key={option}
                                    type="button"
                                    className={`wss-pill${selectedDiningOption === option ? ' active' : ''}`}
                                    data-option={option}
                                    aria-pressed={selectedDiningOption === option}
                                    onClick={() => chooseDiningOption(option)}
                                >
                                    <span className="wss-pill-label">{optionLabel(option)}</span>
                                    <span className="wss-pill-go">{ChevronNextIcon}</span>
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div className="wss-empty">
                            <div className="wss-empty-t">{t('no_table_dining_options')}</div>
                            <div className="wss-empty-s">{t('no_table_dining_options_hint')}</div>
                        </div>
                    )}
                </div>
            </div>

            {tableOpen && (
                <>
                    <button
                        type="button"
                        className="wss-cartbar aq-liquid"
                        data-testid="waiter-cart-bar"
                        aria-expanded={cartOpen}
                        aria-label={t('view_order')}
                        onClick={() => setCartOpen(true)}
                    >
                        <span className="wss-cb-count">{cartCount}</span>
                        <span className="wss-cb-lines">
                            <span className="wss-cb-l1">{t('current_order')}</span>
                            <span className="wss-cb-l2 money">{money(currency, cartTotal)}</span>
                        </span>
                        <span className="wss-cb-go">
                            {t('view_order')}
                            {ChevronUpIcon}
                        </span>
                    </button>

                    {cartOpen && (
                        <>
                            <div
                                className="wss-scrim"
                                data-testid="waiter-cart-scrim"
                                onClick={() => setCartOpen(false)}
                            />
                            <div
                                className="wss-sheet"
                                data-testid="waiter-cart-sheet"
                                role="dialog"
                                aria-modal="true"
                                aria-label={t('current_order')}
                            >
                                <button
                                    type="button"
                                    className="wss-grab"
                                    aria-label={t('close')}
                                    onClick={() => setCartOpen(false)}
                                >
                                    <i />
                                </button>
                                <div className="wss-sheet-body">
                                    <CartPanel
                                        mode="waiter"
                                        inSheet
                                        builder={builder}
                                        promo={{
                                            appliedPromo: null,
                                            allEligibleOptions: [],
                                            showPromoModal: false,
                                            setShowPromoModal: () => {},
                                            selectPromo: () => {},
                                            removePromo: () => {},
                                            promoDiscountAmount: 0,
                                        }}
                                        currency={currency}
                                        tenantConfig={tenantConfig}
                                        selectedDiningOption={selectedDiningOption}
                                        diningOptionObjects={diningOptionObjects}
                                        availableTableNumbers={availableTableNumbers}
                                        tableNumber={tableNumber}
                                        onCharge={handleSend}
                                        onClearBasket={() => builder.setSelectedProducts([])}
                                        chargeDisabled={submitting || cartCount === 0}
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    );
}
